import React from 'react';
import DirectorModal from './DirectorModal';

const AboutDirectors = () => {
  const [selectedDirector, setSelectedDirector] = React.useState(null);
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [hoveredIndex, setHoveredIndex] = React.useState(null);
  const [language, setLanguage] = React.useState(
    localStorage.getItem('language') || 'en'
  );

  React.useEffect(() => {
    const handleLanguageChange = (event) => {
      setLanguage(event.detail.language);
    };
    window.addEventListener('languageChange', handleLanguageChange);
    return () => window.removeEventListener('languageChange', handleLanguageChange);
  }, []);

  const t = (key) => {
    const translations = window.translations || {};
    return translations[language]?.[key] || key;
  };

  const parseDirectorSectionsFromCms = React.useMemo(() => {
    const container = document.getElementById('about-directors-cms-content');
    if (!container) {
      return { jgm: [] };
    }

    const sections = { jgm: [] };
    const headingMap = {
      JGM: 'jgm',
      STOP: null
    };

    let currentSection = null;

    const normalizeHeading = (value = '') =>
      String(value)
        .trim()
        .toUpperCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/\s+/g, '');

    const appendIfValid = (text) => {
      const cleanText = String(text || '').replace(/\s+/g, ' ').trim();
      if (!cleanText || !currentSection) {
        return;
      }
      sections[currentSection].push(cleanText);
    };

    Array.from(container.childNodes).forEach((node) => {
      if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node;
        const text = (element.textContent || '').trim();
        const marker = headingMap[normalizeHeading(text)];

        if (Object.prototype.hasOwnProperty.call(headingMap, normalizeHeading(text))) {
          currentSection = marker;
          return;
        }

        if (/^H[1-6]$/.test(element.tagName)) {
          return;
        }

        appendIfValid(text);
      } else if (node.nodeType === Node.TEXT_NODE) {
        const lines = String(node.textContent || '').split(/\n+/);
        lines.forEach((line) => {
          const marker = headingMap[normalizeHeading(line)];
          if (Object.prototype.hasOwnProperty.call(headingMap, normalizeHeading(line))) {
            currentSection = marker;
            return;
          }
          appendIfValid(line);
        });
      }
    });

    return sections;
  }, []);

  const directors = [
    {
      name: 'João Garcia Miguel',
      image: 'https://i.imgur.com/017UP7P.jpeg',
      titleKey: 'Founder & Artistic Director',
      bioKey: 'joao_bio_1\njoao_bio_2\njoao_bio_3',
      bioParagraphs: parseDirectorSectionsFromCms.jgm,
      linkedin: 'https://www.linkedin.com/in/joaogarciamiguel',
      email: 'joao@example.com'
    }
  ];

  const openModal = (director) => {
    setSelectedDirector(director);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setTimeout(() => setSelectedDirector(null), 300);
  };

  return (
    <>
      <style>
        {`
          .team-carousel-container {
            display: flex;
            gap: 20px;
            justify-content: center;
          }
          
          .profile-card {
            text-align: center;
            width: 100%;
            max-width: 250px;
            margin: 0 auto;
            flex-shrink: 0;
            cursor: pointer;
            transition: transform 0.3s ease;
            position: relative;
          }
          
          .profile-card:hover {
            transform: scale(1.05);
          }
          
          .profile-card .illustration-wrapper {
            border-bottom: 1px solid #111;
            padding-bottom: 15px;
            margin-bottom: 15px;
            transition: border-color 0.3s ease;
          }
          
          .profile-card:hover .illustration-wrapper {
            border-color: #111;
          }
          
          .profile-card img {
            width: 150px;
            height: 150px;
            border-radius: 50%;
            object-fit: cover;
            transition: transform 0.3s ease, box-shadow 0.3s ease;
          }
          
          .profile-card:hover img {
            transform: scale(1.05);
            box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12);
          }
          
          .profile-card .name {
            font-family: 'Libre Baskerville', serif;
            font-size: 20px;
            font-weight: 700;
            color: #111;
            margin: 0;
            transition: color 0.3s ease;
          }
          
          .profile-card:hover .name {
            color: #111;
          }
          
          .profile-card .title {
            font-size: 14px;
            color: #777;
            margin-top: 4px;
            transition: color 0.3s ease;
          }
          
          .profile-card:hover .title {
            color: #555;
          }
          
          .director-click-hint {
            position: absolute;
            top: -10px;
            left: 50%;
            transform: translateX(-50%) translateY(-10px);
            background: rgba(30, 30, 30, 0.92);
            padding: 0.4rem 0.8rem;
            border-radius: 4px;
            font-size: 0.72rem;
            font-weight: 500;
            color: #fff;
            letter-spacing: 0.03em;
            opacity: 0;
            transition: all 0.3s ease;
            pointer-events: none;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18);
            z-index: 10;
            white-space: nowrap;
          }
          
          .profile-card:hover .director-click-hint {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
          }
          
          @media (max-width: 768px) {
            .team-carousel-container {
              flex-direction: column;
              align-items: center;
              gap: 50px;
            }
            .profile-card {
              width: 80%;
              max-width: 300px;
            }
          }
        `}
      </style>
      <div className="team-carousel-container">
        {directors.map((director, index) => (
          <div 
            key={index}
            className="profile-card"
            onClick={() => openModal(director)}
            onMouseEnter={() => setHoveredIndex(index)}
            onMouseLeave={() => setHoveredIndex(null)}
            role="button"
            tabIndex={0}
            onKeyPress={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                openModal(director);
              }
            }}
            aria-label={`View ${director.name}'s profile`}
          >
            <span className="director-click-hint">Click to view profile</span>
            <div className="illustration-wrapper">
              <img 
                src={director.image} 
                alt={`Illustration of ${director.name}`}
              />
            </div>
            <h3 className="name">{director.name}</h3>
            <p className="title">{t(director.titleKey)}</p>
          </div>
        ))}
      </div>
      <DirectorModal 
        director={selectedDirector}
        isOpen={isModalOpen}
        onClose={closeModal}
      />
    </>
  );
};

export default AboutDirectors;
